import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Boxes, CheckCircle2, History, Minus, PackageMinus,
  PackagePlus, Plus, RefreshCw, Search, SlidersHorizontal, Store, XCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const statusMeta = {
  healthy: { label: "Saludable", className: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  low_stock: { label: "Stock bajo", className: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  out_of_stock: { label: "Agotado", className: "text-red-300 border-red-400/30 bg-red-400/10" },
};

const movementLabel = {
  sale: "Venta",
  manual_add: "Entrada",
  manual_remove: "Salida",
  manual_set: "Ajuste exacto",
  manual_set_legacy: "Ajuste compatible",
};

const key = () => globalThis.crypto?.randomUUID?.()
  || `inventory-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const CreatorInventory = () => {
  const [data, setData] = useState({ summary: {}, products: [], movements: [] });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [operation, setOperation] = useState("add");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [thresholdDrafts, setThresholdDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/api/creator/marketplace/inventory");
      setData(payload);
      setThresholdDrafts(Object.fromEntries((payload.products || []).map((product) => [
        product.slug, product.low_stock_threshold ?? 5,
      ])));
      if (selected) {
        const current = (payload.products || []).find((product) => product.slug === selected.slug);
        if (current) setSelected(current);
      }
    } catch (error) {
      setNotice({ type: "error", text: formatApiError(error) });
    } finally {
      setLoading(false);
    }
  }, [selected?.slug]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data.products || []).filter((product) => {
    const matches = `${product.name} ${product.category} ${product.slug}`
      .toLowerCase().includes(query.trim().toLowerCase());
    return matches && (filter === "all" || product.inventory_status === filter);
  }), [data.products, filter, query]);

  const openAdjustment = (product, nextOperation = "add") => {
    setSelected(product);
    setOperation(nextOperation);
    setQuantity(nextOperation === "set" ? Number(product.stock || 0) : 1);
    setReason("");
    setNotice({ type: "", text: "" });
  };

  const adjust = async () => {
    if (!selected || busy) return;
    const amount = Number(quantity);
    if (!Number.isInteger(amount) || amount < 0 || (operation !== "set" && amount === 0)) {
      setNotice({ type: "error", text: "Usa una cantidad entera válida." });
      return;
    }
    if (reason.trim().length < 3) {
      setNotice({ type: "error", text: "Indica el motivo del movimiento." });
      return;
    }

    setBusy(selected.slug);
    const idempotencyKey = key();
    try {
      await api.post(`/api/creator/marketplace/products/${selected.slug}/inventory/adjust`, {
        operation,
        quantity: amount,
        expected_version: Number(selected.inventory_version || 0),
        reason: reason.trim(),
        idempotency_key: idempotencyKey,
      });
      setSelected(null);
      setNotice({ type: "ok", text: "Movimiento registrado. El inventario quedó sincronizado." });
      await load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "INVENTORY_VERSION_CONFLICT") {
        setNotice({
          type: "error",
          text: "El stock cambió mientras lo editabas. Ya actualizamos la información; revisa y vuelve a intentar.",
        });
        await load();
      } else {
        setNotice({ type: "error", text: formatApiError(error) });
      }
    } finally {
      setBusy("");
    }
  };

  const saveThreshold = async (product) => {
    const threshold = Number(thresholdDrafts[product.slug]);
    if (!Number.isInteger(threshold) || threshold < 0) {
      setNotice({ type: "error", text: "El umbral debe ser un entero igual o mayor que cero." });
      return;
    }
    setBusy(`threshold:${product.slug}`);
    try {
      await api.patch(`/api/creator/marketplace/products/${product.slug}/inventory/threshold`, { threshold });
      setNotice({ type: "ok", text: "Alerta de stock actualizada." });
      await load();
    } catch (error) {
      setNotice({ type: "error", text: formatApiError(error) });
    } finally {
      setBusy("");
    }
  };

  const summary = data.summary || {};

  return <PageShell
    kicker="Creador · Marketplace KD9"
    title="INVENTARIO KD9"
    desc="Stock real con control de concurrencia, alertas y trazabilidad de cada entrada, salida y venta."
    testId="creator-inventory-page"
    badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2" aria-label="Actualizar inventario"><RefreshCw size={15} /></button>}
  >
    <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
      {[
        ["Productos", summary.products || 0, Store],
        ["Unidades", summary.units_on_hand || 0, Boxes],
        ["Saludables", summary.healthy || 0, CheckCircle2],
        ["Stock bajo", summary.low_stock || 0, AlertTriangle],
        ["Agotados", summary.out_of_stock || 0, XCircle],
      ].map(([label, value, Icon]) => <div key={label} className="kd9-card p-4">
        <Icon size={17} className="text-cyan-300" />
        <strong className="mt-2 block font-cinzel text-2xl">{value}</strong>
        <div className="mt-1 font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
      </div>)}
    </section>

    {notice.text && <div className="kd9-card mb-5 p-4 font-mont text-sm" style={{ color: notice.type === "error" ? "#fca5a5" : "#86efac" }}>{notice.text}</div>}

    <section className="kd9-card mb-5 grid gap-3 p-3 sm:grid-cols-[1fr_auto]">
      <label className="relative">
        <Search size={16} className="absolute left-3 top-3 text-cyan-400" />
        <input className="kd9-input pl-10" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar producto, categoría o slug" />
      </label>
      <select className="kd9-input min-w-44 px-3" value={filter} onChange={(event) => setFilter(event.target.value)}>
        <option value="all">Todo el inventario</option>
        <option value="healthy">Saludable</option>
        <option value="low_stock">Stock bajo</option>
        <option value="out_of_stock">Agotado</option>
      </select>
    </section>

    {selected && <section className="kd9-card mb-5 border-cyan-400/30 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mont text-[9px] uppercase tracking-widest text-cyan-300">Movimiento de inventario</div>
          <h2 className="mt-1 font-cinzel text-xl">{selected.name}</h2>
          <p className="mt-1 font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
            Stock actual {selected.stock} · versión {selected.inventory_version}
          </p>
        </div>
        <button onClick={() => setSelected(null)} className="kd9-btn-outline rounded-lg p-2" aria-label="Cerrar ajuste"><XCircle size={16} /></button>
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-3">
        <label className="font-mont text-[9px] uppercase tracking-widest">Operación
          <select className="kd9-input mt-2 px-3" value={operation} onChange={(event) => {
            const value = event.target.value;
            setOperation(value);
            if (value === "set") setQuantity(Number(selected.stock || 0));
          }}>
            <option value="add">Agregar unidades</option>
            <option value="remove">Retirar unidades</option>
            <option value="set">Conteo físico exacto</option>
          </select>
        </label>
        <label className="font-mont text-[9px] uppercase tracking-widest">Cantidad
          <input type="number" min="0" step="1" className="kd9-input mt-2 pl-3" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </label>
        <label className="font-mont text-[9px] uppercase tracking-widest">Motivo
          <input className="kd9-input mt-2 pl-3" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Ej. Reposición proveedor" />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {operation !== "set" && [1, 5, 10].map((amount) => <button key={amount} onClick={() => setQuantity(amount)} className="kd9-btn-outline rounded-lg px-3 py-2 font-mont text-[9px]">× {amount}</button>)}
        <button disabled={busy === selected.slug} onClick={adjust} className="kd9-btn-primary ml-auto rounded-lg px-5 py-3 font-mont text-[10px]">
          {busy === selected.slug ? "Registrando…" : "Confirmar movimiento"}
        </button>
      </div>
      <p className="mt-3 font-mont text-[9px]" style={{ color: "var(--kd9-text-soft)" }}>
        El ajuste usa la versión visible. Si entra una venta al mismo tiempo, KD9 rechaza el valor obsoleto y obliga a recargar antes de tocar stock.
      </p>
    </section>}

    {loading ? <div className="kd9-card p-10 text-center">Sincronizando inventario real…</div> : rows.length === 0
      ? <div className="kd9-card p-10 text-center"><Boxes className="mx-auto text-cyan-300" /><div className="mt-3 font-cinzel text-xl">No hay productos en este filtro</div></div>
      : <section className="grid gap-3 lg:grid-cols-2">{rows.map((product) => {
        const meta = statusMeta[product.inventory_status] || statusMeta.healthy;
        return <article key={product.slug} className="kd9-card overflow-hidden">
          <div className="grid grid-cols-[92px_1fr] sm:grid-cols-[120px_1fr]">
            {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="h-full min-h-40 w-full object-cover" /> : <div className="grid min-h-40 place-items-center" style={{ background: "var(--kd9-bg-2)" }}><Store className="text-cyan-300" /></div>}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><div className="font-mont text-[9px] uppercase tracking-widest kd9-gold-text">{product.category}</div><h2 className="mt-1 truncate font-cinzel text-lg">{product.name}</h2></div>
                <span className={`shrink-0 rounded-full border px-2 py-1 font-mont text-[8px] uppercase ${meta.className}`}>{meta.label}</span>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div><div className="font-cinzel text-3xl">{product.stock}</div><div className="font-mont text-[8px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>unidades disponibles</div></div>
                <div className="text-right font-mont text-[9px]" style={{ color: "var(--kd9-text-soft)" }}>Versión<br /><strong className="text-white">{product.inventory_version}</strong></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => openAdjustment(product, "add")} className="kd9-btn-outline rounded-lg px-3 py-2 font-mont text-[9px]"><PackagePlus size={12} className="mr-1 inline" />Entrada</button>
                <button onClick={() => openAdjustment(product, "remove")} disabled={product.stock <= 0} className="kd9-btn-outline rounded-lg px-3 py-2 font-mont text-[9px] disabled:opacity-40"><PackageMinus size={12} className="mr-1 inline" />Salida</button>
                <button onClick={() => openAdjustment(product, "set")} className="kd9-btn-outline rounded-lg px-3 py-2 font-mont text-[9px]"><SlidersHorizontal size={12} className="mr-1 inline" />Conteo</button>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t pt-3" style={{ borderColor: "var(--kd9-border)" }}>
                <AlertTriangle size={13} className="text-amber-300" />
                <span className="font-mont text-[9px]">Avisar en ≤</span>
                <input type="number" min="0" className="kd9-input h-9 w-20 px-2 text-center" value={thresholdDrafts[product.slug] ?? 5} onChange={(event) => setThresholdDrafts({ ...thresholdDrafts, [product.slug]: event.target.value })} />
                <button disabled={busy === `threshold:${product.slug}`} onClick={() => saveThreshold(product)} className="kd9-btn-outline ml-auto rounded-lg px-3 py-2 font-mont text-[8px]">Guardar alerta</button>
              </div>
            </div>
          </div>
        </article>;
      })}</section>}

    <section className="kd9-card mt-6 overflow-hidden">
      <div className="flex items-center gap-2 border-b p-5" style={{ borderColor: "var(--kd9-border)" }}><History size={17} className="text-cyan-300" /><div className="font-cinzel text-xl">Historial de movimientos</div></div>
      {(data.movements || []).length ? <div className="divide-y" style={{ borderColor: "var(--kd9-border)" }}>{data.movements.slice(0, 30).map((movement) => <div key={movement.id} className="grid gap-2 p-4 sm:grid-cols-[1fr_auto_auto] sm:items-center">
        <div><div className="font-cinzel">{movement.product_name}</div><div className="font-mont text-[9px]" style={{ color: "var(--kd9-text-soft)" }}>{movementLabel[movement.movement_type] || movement.movement_type} · {movement.reason}</div></div>
        <div className={`font-cinzel text-lg ${movement.delta > 0 ? "text-emerald-300" : "text-amber-300"}`}>{movement.delta > 0 ? "+" : ""}{movement.delta}</div>
        <div className="font-mont text-[9px] text-right" style={{ color: "var(--kd9-text-soft)" }}>{movement.stock_before} → {movement.stock_after}<br />v{movement.version_before} → v{movement.version_after}</div>
      </div>)}</div> : <div className="p-8 text-center font-cormorant text-lg" style={{ color: "var(--kd9-text-soft)" }}>Los movimientos nuevos aparecerán aquí.</div>}
    </section>
  </PageShell>;
};

export default CreatorInventory;
